import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  ProcessFlowchartValidationError,
  ProcessFlowchartObjectType,
  FlowchartValidationObjectType,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {FLOWCHART_SIDEPANEL_Z_INDEX} from 'ui-library/reactflow/sidepanels/FlowchartSidebar';

export interface FlowchartLockedBannerProps {
  locked: boolean;
  lockErrorMessage?: string;
}

function FlowchartLockedBanner({locked, lockErrorMessage}: FlowchartLockedBannerProps): JSX.Element | null {
  if (!locked || lockErrorMessage == null) {
    return null;
  }

  return <InlineBanner bannerType={InlineBannerType.WARNING} text={lockErrorMessage} />;
}

export interface FlowchartValidationErrorBannerProps {
  validationErrors: Array<ProcessFlowchartValidationError>;
  editorMode: FlowchartEditorMode;
}

const EDITOR_MODE_TO_TOP_LEVEL_OBJECT_TYPE: Record<FlowchartEditorMode, ProcessFlowchartObjectType> = {
  [FlowchartEditorMode.MAIN_CANVAS]: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
  [FlowchartEditorMode.SINGLE_TRANSITION_CANVAS]: ProcessFlowchartObjectType.TRANSITION,
  [FlowchartEditorMode.ACM_EDITOR]: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
  [FlowchartEditorMode.PCM_EDITOR]: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
};

function FlowchartValidationErrorBanner({
  validationErrors,
  editorMode,
}: FlowchartValidationErrorBannerProps): JSX.Element | null {
  const flowchartLevelErrors = validationErrors.filter(
    (error) => error.object?.type === EDITOR_MODE_TO_TOP_LEVEL_OBJECT_TYPE[editorMode] || error.object == null
  );

  if (flowchartLevelErrors.length === 0) {
    return null;
  }

  return (
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      text={flowchartLevelErrors.map((error) => error.errorMessage).join('\n')}
    />
  );
}

export interface FlowchartLimitsBannerProps {
  limitsErrorMessage?: string;
}

function FlowchartLimitsBanner({limitsErrorMessage}: FlowchartLimitsBannerProps): JSX.Element | null {
  if (limitsErrorMessage == null) {
    return null;
  }

  return <InlineBanner bannerType={InlineBannerType.WARNING} text={limitsErrorMessage} />;
}

export interface FlowchartInfoBannersProps {
  messages?: Array<string>;
}

function FlowchartInfoBanners({messages}: FlowchartInfoBannersProps): JSX.Element | null {
  if (messages == null || messages.length === 0) {
    return null;
  }

  return (
    <>
      {messages.map((message, index) => (
        <InlineBanner key={`${index}-${message}`} bannerType={InlineBannerType.INFO} text={message} />
      ))}
    </>
  );
}

export interface ProcessFlowchartBannersProps {
  locked: boolean;
  editorMode: FlowchartEditorMode;
  lockErrorMessage?: string;
  validationErrors: Array<ProcessFlowchartValidationError>;
  limitsErrorMessage?: string;
  infoBannerMessages?: Array<string>;
}

const FLOWCHART_LEVEL_ERROR_OBJECT_TYPES: Array<FlowchartValidationObjectType> = [
  ProcessFlowchartObjectType.FLOWCHART_CONFIG,
  ProcessFlowchartObjectType.TRANSITION,
];

export function ProcessFlowchartBanners({
  locked,
  editorMode,
  lockErrorMessage,
  validationErrors,
  limitsErrorMessage,
  infoBannerMessages,
}: ProcessFlowchartBannersProps): JSX.Element | null {
  const showLockedBanner = locked && lockErrorMessage != null;
  const flowchartLevelErrors = validationErrors.filter(
    (error) => error.object == null || FLOWCHART_LEVEL_ERROR_OBJECT_TYPES.includes(error.object?.type)
  );
  const showValidationBanner = flowchartLevelErrors.length > 0;
  const showLimitsBanner = limitsErrorMessage != null;
  const showInfoBanners = infoBannerMessages != null && infoBannerMessages.length > 0;

  if (!(showLockedBanner || showValidationBanner || showLimitsBanner || showInfoBanners)) {
    return null;
  }

  return (
    <div className={css(styles.bannersContainer)}>
      <FlowchartLockedBanner locked={locked} lockErrorMessage={lockErrorMessage} />
      <FlowchartValidationErrorBanner validationErrors={validationErrors} editorMode={editorMode} />
      <FlowchartLimitsBanner limitsErrorMessage={limitsErrorMessage} />
      <FlowchartInfoBanners messages={infoBannerMessages} />
    </div>
  );
}

const styles = StyleSheet.create({
  bannersContainer: {
    position: 'absolute',
    top: '16px',
    left: '16px',
    zIndex: FLOWCHART_SIDEPANEL_Z_INDEX - 1,
    maxWidth: '800px',
    minWidth: '500px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    whiteSpace: 'pre-line',
  },
});
